import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import NewsCard from '@/pages/news/components/NewsCard';
import Pagination from '@/pages/category/components/Pagination';
import { useSeo } from '@/hooks/useSeo';
import { newsCategories } from '@/mocks/news';
import { getAllNews } from '@/utils/media';

const PAGE_SIZE = 4;
const tabs = ['全部', ...newsCategories];

export default function NewsListPage() {
  const [params, setParams] = useSearchParams();

  useSeo({
    title: '影视资讯 - 黄色影视',
    description: '黄色影视资讯频道，提供最新影视新闻、影评、新片推荐与行业动态，了解影视圈最新风向。',
    keywords: '影视资讯,影评,新片推荐,行业动态,黄色影视',
  });

  const category = params.get('category') ?? '全部';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const allNews = getAllNews();
  const filtered = useMemo(
    () => (category === '全部' ? allNews : allNews.filter((item) => item.category === category)),
    [category, allNews],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, category]);

  const selectCategory = (next: string) => {
    const nextParams = new URLSearchParams();
    if (next !== '全部') nextParams.set('category', next);
    setParams(nextParams, { replace: true });
  };

  const handlePage = (nextPage: number) => {
    const nextParams = new URLSearchParams(params);
    if (nextPage <= 1) nextParams.delete('page');
    else nextParams.set('page', String(nextPage));
    setParams(nextParams, { replace: true });
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1400px] px-4 py-8 md:px-6 md:py-12">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-500">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800">影视资讯</span>
        </nav>

        <header className="mt-4 border-b border-background-200/70 pb-6">
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">影视资讯</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
            汇聚影评、新片推荐与行业动态，带你读懂每一部作品背后的故事与趋势。
          </p>
        </header>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {tabs.map((tab) => {
            const active = tab === category;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => selectCategory(tab)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-primary-500 font-medium text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200 hover:text-foreground-950'
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        <div className="mt-6 space-y-5">
          {paged.length ? (
            paged.map((item) => <NewsCard key={item.id} item={item} />)
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-newspaper-line text-2xl"></i>
              </span>
              <p className="mt-4 text-sm text-foreground-600">暂无相关影视资讯</p>
              <button
                type="button"
                onClick={() => selectCategory('全部')}
                className="mt-4 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line"></i>
                查看全部资讯
              </button>
            </div>
          )}
        </div>

        <Pagination page={currentPage} totalPages={totalPages} onChange={handlePage} />
      </div>
    </SiteLayout>
  );
}